/**
 * Shared domain types for the claims pipeline.
 * Single source of truth — no Deno stdlib or npm imports.
 * Consumed by: supabase/functions/claim-checking (Deno), platform (Vite).
 */

import type { StoredJournal } from "../journal.ts";
import type { Locale } from "./locale.ts";

// ── Core domain types ──────────────────────────────────────────────────────────

/** What the judge concludes about one source: its stance on the claim, or that it doesn't study it. */
export const SOURCE_VERDICTS = ["SUPPORTS", "CONTRADICTS", "INCONCLUSIVE", "IRRELEVANT"] as const;
export type SourceVerdict = (typeof SOURCE_VERDICTS)[number];
/** The verdict of a source that counts as evidence; an irrelevant source never does. */
export type EvidenceVerdict = Exclude<SourceVerdict, "IRRELEVANT">;

/** Whether a value is one of the verdicts the judge can return. */
export function isSourceVerdict(value: unknown): value is SourceVerdict {
  return (SOURCE_VERDICTS as readonly unknown[]).includes(value);
}

/** Whether a judged source studies the claim, and so counts as evidence for it. */
export function isEvidenceVerdict(verdict: SourceVerdict): verdict is EvidenceVerdict {
  return verdict !== "IRRELEVANT";
}

export interface SourceJudgment {
  source_id: string;
  title: string;
  verdict: SourceVerdict;
  /** Verbatim excerpts from the judge-set sections that support the verdict. */
  quotes: string[];
}

/** The knowledge domains a claim can have, in display order. */
export const CLAIM_DOMAINS = [
  "health_and_nutrition",
  "climate_and_environment",
  "equality_and_discrimination",
  "other",
] as const;

/** Top-level knowledge domain of a claim: its topic, named for everything it covers. */
export type ClaimDomain = (typeof CLAIM_DOMAINS)[number];

/** Exhaustive set of valid ClaimDomain values — single source of truth for domain validation. */
export const VALID_CLAIM_DOMAINS: ReadonlySet<ClaimDomain> = new Set(CLAIM_DOMAINS);

/** Whether a string names a claim domain. */
export function isClaimDomain(value: string): value is ClaimDomain {
  return (CLAIM_DOMAINS as readonly string[]).includes(value);
}

/** What a domain covers. A domain is the claim's topic, not whether it states an outcome: the kind
 *  carries that, so "coconut oil is 90% saturated fat" is health_and_nutrition, an attribute. */
export const CLAIM_DOMAIN_DEFINITIONS: Record<ClaimDomain, string> = {
  health_and_nutrition:
    "Human or animal health and nutrition: the body, disease, medicine, medication, mental health, vaccines, food and what it contains.",
  climate_and_environment:
    "Climate and the environment: emissions, energy, warming, ecosystems, pollution, waste, land use.",
  equality_and_discrimination:
    "How groups of people are treated, by the protected characteristics of the UK Equality Act: age, disability, gender reassignment, marriage and civil partnership, pregnancy and maternity, race, religion or belief, sex, sexual orientation. Discrimination, harassment, pay gaps and unequal outcomes between groups.",
  other: "Anything else: history, geography, technology, culture, sport, or no subject at all.",
};

/** Applied before a claim is labelled: who says, believes or recommends it is not what is checked. */
export const CLAIM_FRAME_STRIPPING =
  "Strip who says, believes or recommends the claim, official bodies included ('I think', 'people say', 'studies show', 'according to', 'doctors recommend', 'the WHO recommends'), and label what remains. A statement about the evidence for a claim is stripped to the claim, since checking the evidence is our job: 'there is no evidence that seed oils cause inflammation' is seed oils → inflammation. A prescription or recommendation with a stated purpose or reason is the relation it carries, even one the speaker gives themself: 'governments need to ban single-use plastics to save our oceans' is banning single-use plastics → the oceans, 'women shouldn't work in construction because it's too dangerous for them' is construction work → women's safety. A health prescription without a stated purpose takes health as its purpose: 'you must floss every day' is flossing every day → health. Only a prescription that rests on values alone, with no purpose or reason, is personal. Truth does not matter: a false, absurd or conspiratorial claim still has a kind.";

/**
 * What kind of claim it is, with each kind's subkinds. The Understanding Claims product spec holds
 * the taxonomy and its reasons. Extraction skips personal claims and leaves the kind for a later
 * classifier, so nothing a user sees depends on it today.
 */
export const CLAIM_KIND_TAXONOMY = {
  relation: {
    definition:
      "One subject related to another. The kind is decided by how many things vary, never by a number: a rate, a share or an accuracy is a modifier any kind can carry, so two linked things make a relation whatever number comes with them ('people with disabilities are unemployed at twice the rate', 'over a million species are threatened by habitat loss'). Comparing two subjects does not make a relation: a comparison takes the kind of the dimension it compares on ('butter is healthier than seed oils' is a directed relation, each subject acting on health; 'whole wheat flour has more fibre than white flour' an attribute).",
    subkinds: {
      directed:
        "An exposure changes an outcome: 'seed oils cause inflammation', 'replacing butter lowers LDL'. Terms that name a health effect count without a verb, however strongly worded ('X is toxic', 'X is healthy', 'X is terrible for your health'): a judgement on health is a relation, not a taste. A condition that makes an instrument wrong is an exposure too: 'prick tests are less reliable in people on antihistamines'.",
      undirected:
        "Two things vary together across people, places or time, with no direction claimed: 'hypertension is correlated with sugar intake', 'people who sleep less weigh more'. So does a sign or marker said to reveal a condition, and what an instrument (a test, device, model or method) detects or predicts, how often right or wrong included, since one is read to know the other: 'fingernail appearance reveals nutritional deficiencies', 'DNA tests tell you your ethnic heritage', 'PCR gives false positives'.",
    },
  },
  attribute: {
    definition:
      "The subject itself: what it is, what happens or exists, or what people decided about it.",
    subkinds: {
      nature:
        "What something is: what a physical thing is made of or has, what a concept means or how it is classified: 'Prozac contains fluoxetine', 'the earth is flat', 'vitamin D is a hormone'.",
      occurrence:
        "What happens or exists in the world, about one subject: events, dates, an actor's conduct (a body deciding or stating something included), what people do, and how common one thing is: '1 in 3 adults have hypertension', 'Big Pharma hides cures'.",
      convention:
        "What is so because people decided it, relative to who decides: what a law, regulation or authority requires, allows or bans, and customs and standards: 'fluoride is banned in Europe', 'a marathon is 42.195 km'. A body deciding or stating something, as an event, is an occurrence ('the FDA approved X in 2019').",
    },
  },
  personal: {
    definition:
      "Only the speaker can settle it: tastes, values, faith, anecdotes, their own habits: 'Eminem is the best rapper', 'the Olympics should be held in one city', 'I never eat gluten'. Not verified.",
    subkinds: {},
  },
  unclear: {
    definition:
      "More than one claim in one sentence ('meat is bad for your health and the planet'), or a hesitation between kinds. Verified as today, so a hesitation never turns into 'we don't check that'.",
    subkinds: {},
  },
} as const;

export type ClaimKind = keyof typeof CLAIM_KIND_TAXONOMY;

export const CLAIM_KINDS = Object.keys(CLAIM_KIND_TAXONOMY) as ClaimKind[];

type ClaimSubkindOf<K extends ClaimKind> = keyof (typeof CLAIM_KIND_TAXONOMY)[K]["subkinds"];

/** A kind with one of its subkinds, or with null for a kind that has none. */
export type ClaimKindAndSubkind = {
  [K in ClaimKind]: {
    kind: K;
    subkind: [ClaimSubkindOf<K>] extends [never] ? null : ClaimSubkindOf<K>;
  };
}[ClaimKind];

/** A kind and subkind written as one token: "relation.directed", or "personal" for a kind without. */
export type ClaimKindToken = {
  [K in ClaimKind]: [ClaimSubkindOf<K>] extends [never] ? K : `${K}.${ClaimSubkindOf<K> & string}`;
}[ClaimKind];

/** Every kind and subkind, in taxonomy order. */
export const CLAIM_KIND_AND_SUBKINDS: readonly ClaimKindAndSubkind[] = CLAIM_KINDS.flatMap(
  (kind) => {
    const subkinds = Object.keys(CLAIM_KIND_TAXONOMY[kind].subkinds);
    return (subkinds.length ? subkinds : [null]).map(
      (subkind) => ({ kind, subkind }) as ClaimKindAndSubkind
    );
  }
);

/** Writes a kind and subkind as one token. */
export function formatClaimKind({ kind, subkind }: ClaimKindAndSubkind): ClaimKindToken {
  return (subkind === null ? kind : `${kind}.${subkind}`) as ClaimKindToken;
}

/** Reads a token written by `formatClaimKind`; null when it names no kind and subkind. */
export function parseClaimKind(token: string): ClaimKindAndSubkind | null {
  return CLAIM_KIND_AND_SUBKINDS.find((k) => formatClaimKind(k) === token) ?? null;
}

/** What a kind and subkind mean, for people labelling and for prompts: the subkind's definition,
 *  or the kind's when it has none. */
export function describeClaimKind({ kind, subkind }: ClaimKindAndSubkind): string {
  const { definition, subkinds } = CLAIM_KIND_TAXONOMY[kind];
  return subkind === null ? definition : (subkinds as Record<string, string>)[subkind];
}

/**
 * The admission gate's finding types (tangrowth/models/admission.py) that could answer each kind
 * and subkind, so a claim's kind narrows the papers that could settle it. Absent ones no paper answers.
 */
export const CLAIM_KIND_FINDING_TYPES: Partial<Record<ClaimKindToken, readonly string[]>> = {
  "relation.directed": ["health_effect"],
  "relation.undirected": ["health_effect"],
  "attribute.occurrence": ["occurrence", "measurement_validity"],
  "attribute.nature": ["product_or_material", "molecular_characterisation"],
};

/** No kind assigned yet: extraction labels a claim's domain only. */
export interface UnclassifiedClaimKind {
  kind: null;
  subkind: null;
}

/** A claim's properties: what it is about, and what kind of claim it is once classified. */
export type ClaimProperties = { domain: ClaimDomain } & (
  ClaimKindAndSubkind | UnclassifiedClaimKind
);

/** A claim's properties once its kind is assigned, as people label them. */
export type ClassifiedClaimProperties = { domain: ClaimDomain } & ClaimKindAndSubkind;

/** Why a claim is not verified: its topic is not one we check. */
export type ClaimRefusalReason = "outside_health";

/** The domains whose claims are verified: the ones with a corpus. The SQL view
 *  `uncovered_claims` applies the same rule, so a change here is a change there. */
export const VERIFIED_CLAIM_DOMAINS: readonly ClaimDomain[] = ["health_and_nutrition"];

/**
 * The reason a claim is shown and never verified, or null when it goes to verification. A claim
 * without stored properties is verified as before.
 */
export function getClaimRefusal(properties: ClaimProperties | null): ClaimRefusalReason | null {
  if (properties === null) return null;
  return VERIFIED_CLAIM_DOMAINS.includes(properties.domain) ? null : "outside_health";
}

/** Whether a claim goes to verification at all. */
export function isClaimVerifiable(properties: ClaimProperties | null): boolean {
  return getClaimRefusal(properties) === null;
}

/** The domain and kind columns of a claim row, as stored; the kind is a `formatClaimKind` token. */
export interface StoredClaimProperties {
  domain: string | null;
  kind: string | null;
}

/** Writes a claim's kind as stored: its `formatClaimKind` token, or null while unclassified. */
export function formatStoredClaimKind(properties: ClaimProperties): ClaimKindToken | null {
  return properties.kind === null ? null : formatClaimKind(properties);
}

/** Reads the properties stored on a claim; null for a claim stored before its domain was assigned. */
export function parseStoredClaimProperties(stored: StoredClaimProperties): ClaimProperties | null {
  if (stored.domain === null || !isClaimDomain(stored.domain)) return null;
  if (stored.kind === null) return { domain: stored.domain, kind: null, subkind: null };
  const kind = parseClaimKind(stored.kind);
  return kind === null ? null : { domain: stored.domain, ...kind };
}

/** A claim row as stored in the DB — the canonical representation of a factual claim. */
export interface Claim {
  /** DB UUID of the canonical representative claim (the `id` column on `claims`).
   *  Pass this to POST /claim-checking/claims/:claim_id/verifications. */
  id: string;
  /** Self-contained, unambiguous factual statement. Pronouns resolved, hedging removed, active voice. */
  canonical: string;
  /** Domain and kind, each assigned once and never changed by a later extraction. Null for a
   *  claim stored before its domain was assigned, which is verified as before. */
  properties: ClaimProperties | null;
}

/** A single item in the /claim-checking/extract-claims response.
 *  Combines the DB-backed Claim with the verbatim fragment as it appeared in the source. */
export interface ClaimWithSource extends Claim {
  /** Verbatim claim fragment as expressed in the source text. May be a sentence fragment. */
  source_text: string;
  /** Always set: extraction labels the claim's domain if it had none. */
  properties: ClaimProperties;
}

// ── Evidence types (API surface) ───────────────────────────────────────────────

/** Common identity and provenance fields shared across pipeline source representations. */
export interface SourceBase {
  source_id: string;
  title: string;
  url: string | null;
  authors: string | null;
  published_date: string | null;
  source_type: string;
}

/** One source document as it appears in a verification result. Combines source
 *  metadata with the LLM verdict and relevant verbatim quotes. */
export interface EvidenceSource extends SourceBase {
  /** Execution quality within this source's own study-type scoring profile, [0, 1] — not
   *  comparable across designs. Diagnostic — cross_design_quality is the canonical signal. */
  design_execution_quality: number;
  /** Canonical quality signal: design_execution_quality × the study type weight, [0, 1] —
   *  comparable across designs. Drives reranking, the displayed rating bucket, and verdict
   *  aggregation (design-documents/025). */
  cross_design_quality: number;
  scorecard_labels: Record<string, string[]> | null;
  verdict: EvidenceVerdict;
  /** Verbatim excerpts from the judge-set sections that support the verdict. */
  quotes: string[];
}

/** The source metadata a client can read for a verification's evidence, display-only. */
export const EVIDENCE_METADATA_FIELDS = ["publication_types", "journal"] as const;

export type EvidenceMetadataField = (typeof EVIDENCE_METADATA_FIELDS)[number];

/** Whether a string names a readable evidence metadata field. */
export function isEvidenceMetadataField(name: string): name is EvidenceMetadataField {
  return (EVIDENCE_METADATA_FIELDS as readonly string[]).includes(name);
}

/** One evidence source's requested metadata. A field the source does not carry is omitted. */
export interface EvidenceMetadata {
  source_id: string;
  /** PubMed's own publication types, as NCBI spells them. */
  publication_types?: string[];
  journal?: StoredJournal;
}

/** GET /claim-checking/verifications/:id/evidence-metadata — metadata of the verification's evidence, in its own order. */
export interface VerificationEvidenceMetadataResponse {
  items: EvidenceMetadata[];
}

// ── Verification trace types ──────────────────────────────────────────────────

/**
 * Per-source weight breakdown produced during verdict aggregation.
 * Every intermediate factor is preserved for audit and tracing.
 */
export interface WeightedSource {
  source_id: string;
  title: string;
  verdict: EvidenceVerdict;
  /** +1 for SUPPORTS, -1 for CONTRADICTS, 0 for INCONCLUSIVE. */
  vote: 1 | -1 | 0;
  /** Exponential recency decay weight in (0, 1]. Exactly 1.0 when published_date is null. */
  recency_weight: number;
  /** "no published_date" when published_date is null; null otherwise. */
  recency_note: string | null;
  /** Execution quality (diagnostic) — see EvidenceSource.design_execution_quality. */
  design_execution_quality: number;
  /** recency_weight × cross_design_quality (design-documents/025 — no separate methodology
   *  term; the study type weight is already folded into cross_design_quality). */
  base_weight: number;
  /** recency_weight × cross_design_quality (RET-28: source_multiplier removed). */
  weight: number;
  /** weight × vote — this source's signed contribution to the aggregate signal. */
  contribution: number;
}

/** Aggregate signal metrics from the weighted sources. */
export interface SignalSummary {
  total_mass: number;
  /** Contribution-weighted mean in [-1, 1]. */
  signal: number;
  support_mass: number;
  contradict_mass: number;
  conflict_detected: boolean;
}

/**
 * Lightweight reference to a retrieved paragraph, stored in the verification trace.
 * The raw text is reconstructed from Storage using source_id + section coordinates
 * when the trace is served via the internal API.
 * paragraph_text is absent in the stored form and populated by resolveJudgmentTraceParagraphs.
 */
export interface ParagraphJudgmentTrace {
  /** 0-based index in the source group's retrieved paragraphs. */
  paragraph_index: number;
  /** Section this paragraph was retrieved from. */
  section_key: string;
  /** 0-based index among sections sharing the same key within this source. */
  section_ordinal: number;
  /** 0-based paragraph index within the section occurrence (split on \n\n, empty filtered). */
  paragraph_ordinal: number;
  /** Raw source text for this paragraph. Present only after resolveJudgmentTraceParagraphs. */
  paragraph_text?: string;
}

/**
 * Per-source paragraph judgment references stored in the verification trace, one for every
 * source the judge read. WeightedSource holds only the sources that counted as evidence, so the
 * verdict is kept here too: it is the only record of a source judged irrelevant.
 */
export interface SourceJudgmentTrace {
  source_id: string;
  /** Absent on traces recorded before the verdict was kept here. */
  verdict?: SourceVerdict;
  paragraphs: ParagraphJudgmentTrace[];
}

/** Why a candidate source did or didn't make it into the sources passed to the judge step. A
 *  reserve source is judged too, but counts as evidence only in place of an irrelevant one. */
export type CandidateGateOutcome =
  | "selected"
  | "reserve"
  | "dropped_low_similarity"
  | "dropped_semantic_limit"
  | "dropped_low_quality"
  | "dropped_quality_limit";

/** A single candidate paragraph's vector-search score. */
export interface DocumentCandidateParagraphTrace {
  section_key: string;
  section_ordinal: number;
  paragraph_ordinal: number;
  semantic_similarity: number;
}

/**
 * A candidate source document from vector search, with its retrieved paragraphs and the outcome
 * of the gates, which judge sources whole rather than paragraph by paragraph.
 */
export interface DocumentCandidateTrace {
  source_id: string;
  outcome: CandidateGateOutcome;
  /** What ranks the source in the semantic gate: the sum of how far its best paragraphs exceed
   *  the semantic threshold. Null on traces recorded before the gates judged whole sources. */
  document_score: number | null;
  /** Null when the source did not reach the quality gate (it was dropped by the semantic gate). */
  cross_design_quality: number | null;
  paragraphs: DocumentCandidateParagraphTrace[];
}

/**
 * DocumentCandidateTrace as returned by GET /claim-checking/verifications/:id/trace.
 * title/url are resolved from the sources table at read time via a join — every source has a
 * url, and title falls back to "" rather than being omitted when the source has none.
 */
export interface ResolvedDocumentCandidateTrace extends DocumentCandidateTrace {
  title: string;
  url: string;
}

/** Full trace of the vector-search + reranking stage that selects candidate documents. */
export interface DocumentSelectionTrace {
  semantic_threshold: number;
  semantic_limit: number;
  quality_threshold: number;
  quality_limit: number;
  candidates: DocumentCandidateTrace[];
}

/** DocumentSelectionTrace as returned by GET /claim-checking/verifications/:id/trace. */
export interface ResolvedDocumentSelectionTrace extends Omit<DocumentSelectionTrace, "candidates"> {
  candidates: ResolvedDocumentCandidateTrace[];
}

/** LLM judge timing for a single source within a verification run. */
export interface SourceJudgeTiming {
  source_id: string;
  /** Wall-clock milliseconds for the LLM call that judged this source. */
  wall_ms: number;
}

/** Per-step wall-clock timing for one verification pipeline run. */
export interface VerificationTiming {
  /** Time the pipeline waited for the claim embedding after the cache lookup: 0 when the claim
   *  had one stored, less than the embed call when it started alongside the lookup. */
  embed_ms: number;
  /** pgvector RPC search duration. */
  search_ms: number;
  /** Two-stage quality filter (scorecard scoring + gates) duration. */
  rerank_ms: number;
  /** Total wall time for the parallel judge step (= duration of slowest source). */
  judge_ms: number;
  /** Per-source LLM call timing within the judge step. */
  judge_sources: SourceJudgeTiming[];
  /** Total pipeline wall time (embed + search + rerank + judge, before persist). */
  total_ms: number;
  /** LLM provider used for the judge step, e.g. "groq" or "anthropic". */
  provider: string;
  /** Exact model name used for the judge step. Absent for composite clients (e.g. failover). */
  model?: string;
}

/** The time of the stages that fetch a claim's sources. */
export type RetrievalTiming = Pick<VerificationTiming, "embed_ms" | "search_ms" | "rerank_ms">;

/** The time of the stage that judges the sources. */
export type JudgmentTiming = Pick<VerificationTiming, "judge_ms" | "judge_sources">;

/** One source the judge read, with the stance it gave and whether the verdict counts it. */
export interface JudgedSourceOutcome {
  source_id: string;
  title: string;
  verdict: SourceVerdict;
  shown_as_evidence: boolean;
}

/**
 * Full computation trace for a single claim verification.
 * Stored persistently after every pipeline run and retrievable via the internal API.
 */
export interface VerificationTrace {
  /** Human-readable description of the first verdict rule that matched. */
  rule_applied: string;
  /** Per-source weight breakdown for every source that entered aggregation. */
  weighted_sources: WeightedSource[];
  /** Aggregate signal metrics before verdict resolution. */
  signal_summary: SignalSummary;
  /** Per-source LLM judgment with paragraph-level references for raw text reconstruction. */
  source_judgment_traces: SourceJudgmentTrace[];
  /** Vector-search + reranking trace. Null for verifications run before migration 034. */
  document_selection: DocumentSelectionTrace | null;
  /** Per-step pipeline timing. Null for verifications run before migration 20260808090000. */
  timing?: VerificationTiming | null;
}

/**
 * VerificationTrace as returned by GET /claim-checking/verifications/:id/trace.
 * Paragraph texts are resolved from object storage, and candidate titles/urls from the sources
 * table, by the backend before responding.
 * source_judgment_traces is null for verifications run before migration 024.
 */
export interface ResolvedVerificationTrace extends Omit<
  VerificationTrace,
  "source_judgment_traces" | "document_selection"
> {
  source_judgment_traces: SourceJudgmentTrace[] | null;
  document_selection: ResolvedDocumentSelectionTrace | null;
}

// ── Aggregate types ────────────────────────────────────────────────────────────

export type ClaimVerdict =
  | "SUPPORTED"
  | "CONTRADICTED"
  | "LEANING_SUPPORTED"
  | "LEANING_CONTRADICTED"
  | "MIXED"
  /** Evidence we have, enough of it, that points nowhere: most of its weight is inconclusive. */
  | "INCONCLUSIVE"
  /** Some relevant evidence, but too little weight to conclude. */
  | "INSUFFICIENT"
  /** No source counted as evidence: nothing found, or everything found was irrelevant. */
  | "NO_COVERAGE"
  /** @deprecated Replaced by LEANING_SUPPORTED / LEANING_CONTRADICTED. Kept for backward compat with stored rows. */
  | "PARTIALLY_SUPPORTED";

/** The verdicts for which the corpus cannot answer the claim: a coverage gap, whatever it shows. */
export const GAP_VERDICTS = [
  "NO_COVERAGE",
  "INSUFFICIENT",
] as const satisfies readonly ClaimVerdict[];

/** Whether a verdict leaves the claim a coverage gap, so a person can ask to be told when it is answered. */
export function isGapVerdict(verdict: ClaimVerdict): boolean {
  return (GAP_VERDICTS as readonly ClaimVerdict[]).includes(verdict);
}

/**
 * The summed source weight behind a verdict, by direction. A weak signal reads differently with a
 * small total (little evidence) than with large masses on both sides (evidence that disagrees).
 */
export interface EvidenceMass {
  /** Summed weight of the sources that support the claim. */
  support: number;
  /** Summed weight of the sources that contradict it. */
  contradict: number;
  /** Summed weight of every source counted as evidence, inconclusive ones included. */
  total: number;
}

/** Sources counted as evidence, in total and by an INCONCLUSIVE verdict. */
export interface EvidenceCount {
  inconclusive: number;
  total: number;
}

/** How much of our corpus matched a claim, bucketed by what the pipeline did with the matches. */
export type CorpusCoverage = "sparse" | "moderate" | "broad";

export interface ClaimVerdictResult {
  verdict: ClaimVerdict;
  /** Net direction and strength of the evidence, [-1, 1]: (support − contradict) / total. */
  signal: number;
  evidence_mass: EvidenceMass;
  conflict_detected: boolean;
  evidence_count: EvidenceCount;
  /** Sources with a paragraph at or above the semantic threshold among the retrieved candidate
   *  paragraphs — those that could have been evidence. The judged ones are in evidence_count. */
  sources_scanned: number;
}

// ── Fallacy types ──────────────────────────────────────────────────────────────

/** One entry in the fallacy controlled vocabulary (claim-checking/config/fallacies.ts). */
export interface FallacyDefinition {
  tag: string;
  definition: string;
  trigger_example?: string;
}

/** Response shape of POST /claim-checking/verifications/:id/fallacies. The per-claim fallacy analysis
 *  prose is intentionally not surfaced here — it's an internal artifact
 *  consumed by POST /claim-checking/verifications/:id/explanations, where it's integrated into the user-facing
 *  explanation. The fallacy expert is the authority on identifying and
 *  explaining fallacies; the explainer's job is to weave that work into the
 *  broader narrative, not to regenerate it. */
export interface FallacyResult {
  verification_id: string;
  tags: string[];
  /** Plain-language definition for each fired tag, sourced from the controlled
   *  vocabulary on the server. Lets the UI render hover tooltips without
   *  duplicating the taxonomy. Always covers exactly the fired tags. */
  definitions: Record<string, string>;
  cache_hit: boolean;
  /** ISO datetime of when the result was last computed. Present when cache_hit is true. */
  cached_at?: string;
}

// ── Explanation types ──────────────────────────────────────────────────────────

/** Response shape of POST /claim-checking/verifications/:id/explanations. Prose is ≤200 words with inline
 *  [N] references mapping into the verifications endpoint's evidence ordering. */
export interface ExplanationResult {
  /** PK of the explanation_results row — used by the user plane to pin events. */
  id: string;
  verification_id: string;
  /** ≤200-word natural-language explanation with inline [N] references. */
  text: string;
  /** Locale the text is written in. */
  locale: Locale;
  cache_hit: boolean;
  /** ISO datetime of when the result was last computed. Present when cache_hit is true. */
  cached_at?: string;
}

/** One entry of a person's verification history: a question they asked and what they were shown.
 *  `id` opens the result exactly as it was shown then. */
export interface VerificationHistoryItem {
  /** The user_event_verification id: the public id of the result they were shown. */
  id: string;
  /** What they submitted, or the claim's canonical text when that was not kept. */
  claim_text: string;
  /** The verdict they were shown; null for a claim we do not check. */
  verdict: ClaimVerdict | null;
  /** ISO datetime of when they asked. */
  asked_at: string;
}

// ── API response shapes ────────────────────────────────────────────────────────

export interface ExtractResponse {
  claims: ClaimWithSource[];
}

export interface VerifyResponse {
  /** Stable handle for downstream endpoints (fallacy, explain). Regenerates on recompute. */
  verification_id: string;
  claim: Claim;
  claim_verdict: ClaimVerdictResult;
  /** One entry per source — metadata, LLM verdict, and relevant quotes merged. */
  evidence: EvidenceSource[];
  cache_hit: boolean;
  /** ISO datetime of when the result was last computed. Present when cache_hit is true. */
  cached_at?: string;
  /** DB UUID of the claim row. Present on POST .../verifications responses. */
  claim_id?: string;
  /** ID of the user_event_verifications row. Present on POST .../verifications responses. */
  user_event_verification_id?: string;
  /** Verbatim text the user originally submitted. Present on GET .../verifications/:id responses. */
  raw_text?: string | null;
  /** Present on GET .../verifications/:id responses. See VerificationResultResponse. */
  stale_since?: string | null;
}

/** Response of POST /claim-checking/extract-and-verify — the collapsed happy-path endpoint.
 *  `verified` when exactly one of the claims found is one we check (verified inline, no second
 *  round-trip; the others are left out); `ambiguous` for zero claims, or for several we check,
 *  which the caller disambiguates on the review screen and verifies separately; `not_verified`
 *  when claims were found and none is one we check, each carrying its properties for the reason.
 *  `claim` carries the source text the verification result's canonical `claim` omits. */
export type ExtractAndVerifyResponse =
  | { kind: "ambiguous"; claims: ClaimWithSource[] }
  | { kind: "verified"; claim: ClaimWithSource; verification: VerifyResponse }
  | { kind: "not_verified"; claims: ClaimWithSource[] };

export interface FetchUrlResponse {
  text: string;
  title: string | null;
}

/** Response shape of GET /claim-checking/verifications/:id — always a cache hit pinned to a specific verification run. */
export interface VerificationResultResponse {
  verification_id: string;
  claim: Claim;
  claim_id: string;
  claim_verdict: ClaimVerdictResult;
  evidence: EvidenceSource[];
  cache_hit: true;
  cached_at: string;
  /** When new evidence first made this verification no longer reusable; null while it still is.
   *  The verification itself is immutable, so a shared page uses this to warn it may be outdated. */
  stale_since: string | null;
  /** Verbatim text the user originally submitted. Null if the user submitted a URL rather than text. */
  raw_text: string | null;
}

// ── GET /claim-checking/verifications/:id/insights ────────────────────────────

/** One expert insight attached to a verification, in ranked retrieval order. */
export interface AttachedInsight {
  rank: number;
  /** author_id from expert_insights — stable grouping key across insights by the same contributor. */
  authorId: string;
  title: string;
  body: string;
  /** Full name from user_profiles. Null for hand-seeded insights without a full profile. */
  contributorName: string | null;
  /** Credentials from expert_profiles. Null when not set. */
  contributorCredentials: string | null;
  /** Short bio from expert_profiles. Null when not set. */
  contributorBio: string | null;
  /** Avatar URL from user_profiles. Null when not set. */
  contributorAvatarUrl: string | null;
}

// ── GET /claim-checking/verifications/:id/trace ────────────────────────────────

/** Response shape of GET /claim-checking/verifications/:id/trace.
 *  Admin-gated; null trace for verifications run before trace storage was introduced. */
export interface ClaimVerificationTraceResponse {
  claim: Claim;
  claim_verdict: ClaimVerdictResult;
  evidence: EvidenceSource[];
  cached_at: string;
  trace: ResolvedVerificationTrace | null;
}
